import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area 
} from 'recharts';
import { useSearchParams } from 'react-router-dom';
import { 
  Calendar, Filter, Download, TrendingUp, Users, DollarSign, MessageSquare,
  AlertTriangle, RefreshCw, Globe, Zap, Clock, Target, Link,
  ArrowDownLeft, Activity, CheckCircle
} from 'lucide-react';
import { apiCallJson, apiCall } from './Api';
import { useAuth } from './contexts/AuthContext';
import SchedulerWidget from './SchedulerWidget';
import { getCurrentToken, getCurrentSessionId } from './SessionManager';

const UnifiedDashboard = ({ user, onNavigate }) => {
  const { isCustomer } = useAuth();
  const [overviewData, setOverviewData] = useState({});
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [channelData, setChannelData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [performanceTrends, setPerformanceTrends] = useState([]);
  const [kpis, setKpis] = useState({});
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMetric, setActiveMetric] = useState('total_messages');
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialFilters = () => {
    const timeRange = searchParams.get('time_range');
    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');

    if (startDate && endDate) {
      return {
        time_range: 'custom',
        start_date: startDate,
        end_date: endDate
      };
    } else {
      const saved = localStorage.getItem('etl_date_range');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return {
            time_range: parsed.preset || parsed.time_range || 'last_7_days',
            start_date: parsed.start_date || '',
            end_date: parsed.end_date || ''
          };
        } catch (e) {
        }
      }
    }
    
    return {
      time_range: timeRange || 'last_7_days',
      start_date: '',
      end_date: ''
    };
  };
  
  const [filters, setFilters] = useState(getInitialFilters);

  const buildParams = () => {
      if (filters.time_range === 'custom') {
        // Strict validation for custom date range
        if (!filters.start_date || !filters.end_date) {
          console.warn('Custom range selected but dates not complete, falling back to last_7_days');
          return new URLSearchParams({ time_range: 'last_7_days' });
        }
        if (filters.end_date < filters.start_date) {
          console.warn('End date before start date, falling back to last_7_days');
          return new URLSearchParams({ time_range: 'last_7_days' });
        }
        
        // Valid custom range
        return new URLSearchParams({
          start_date: filters.start_date,
          end_date: filters.end_date
        });
      } else {
        // Preset range
        return new URLSearchParams({
          time_range: filters.time_range
        });
      }
    };

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const token = getCurrentToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const params = buildParams();
      setSearchParams(params);

      // Load all dashboard data in parallel
      const [overview, timeSeries, customers, vendors, channels, regions, trends, htableKpis, htableCharts] = await Promise.all([
        apiCall(`/api/dashboard/overview/${user.host_tenant_id}?${params}`, { headers }).then(r => r.json()),
        apiCall(`/api/dashboard/time-series/${user.host_tenant_id}?${params}`, { headers }).then(r => r.json()),
        apiCall(`/api/dashboard/top-customers/${user.host_tenant_id}?limit=5&${params}`, { headers }).then(r => r.json()),
        apiCall(`/api/dashboard/top-vendors/${user.host_tenant_id}?limit=5&${params}`, { headers }).then(r => r.json()),
        apiCall(`/api/dashboard/channel-distribution/${user.host_tenant_id}?${params}`, { headers }).then(r => r.json()),
        apiCall(`/api/dashboard/region-distribution/${user.host_tenant_id}?${params}`, { headers }).then(r => r.json()),
        apiCall(`/api/dashboard/performance-trends/${user.host_tenant_id}?${params}`, { headers }).then(r => r.json()).catch(() => []),
        apiCall(`/api/htable/kpis?${params}`).then(r => r.json()).catch(() => ({})),
        apiCall(`/api/htable/chart-data?${params}`).then(r => r.json()).catch(() => [])
      ]);
      
      setOverviewData(overview);
      setTimeSeriesData(timeSeries);
      setTopCustomers(customers);
      setTopVendors(vendors);
      setChannelData(channels);
      setRegionData(regions);
      setPerformanceTrends(trends);
      setKpis(htableKpis);
      setChartData(htableCharts);
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user.host_tenant_id, filters]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    // Save to localStorage with consistent format
    localStorage.setItem('etl_date_range', JSON.stringify({
      preset: filters.time_range,
      time_range: filters.time_range,
      start_date: filters.start_date,
      end_date: filters.end_date
    }));
  }, [filters]);

  const KPICard = ({ title, value, icon: Icon, color, subtitle, change }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${color} text-white`}>
          <Icon size={24} />
        </div>
        <div className="ml-4 flex-1">
          <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          {change && (
            <div className={`text-sm font-semibold ${
              change.startsWith('+') ? 'text-green-600' : 'text-red-600'
            }`}>
              {change}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const formatValue = (value, type) => {
    if (value === null || value === undefined) return '0';
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
      case 'percentage':
        return `${parseFloat(value).toFixed(1)}%`;
      case 'number':
        return value.toLocaleString();
      case 'time': {
        const ms = parseFloat(value);
        if (!isFinite(ms)) return '0ms';
        if (ms < 1000) return `${ms.toFixed(1)}ms`;       // 0–999ms        → 234.5ms
        if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`;   // 1–59s     → 4.32s
        if (ms < 3_600_000) {                              // 1–59m
          const minutes = Math.floor(ms / 60_000);
          const seconds = Math.round((ms % 60_000) / 1000);
          return seconds === 60 ? `${minutes + 1}m` : `${minutes}m ${seconds}s`;
        }
        const hours = Math.floor(ms / 3_600_000);          // ≥1h
        const minutes = Math.round((ms % 3_600_000) / 60_000);
        return minutes === 60 ? `${hours + 1}h` : `${hours}h ${minutes}m`;
      }
      default:
        return value;
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const handleDrillDown = (dimension, value) => {
    onNavigate('analytics', { dimension, value });
  };

  const exportData = (format) => {
    console.log(`Exporting dashboard data as ${format}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Unified KPI data combining both sources with priority to overview data
  const unifiedKpiCards = [
    {
      title: 'Total Messages',
      value: formatNumber(overviewData.total_messages || kpis.total_messages || 0),
      icon: MessageSquare,
      color: 'bg-blue-500',
      change: '+12%',
      subtitle: `${formatNumber(kpis.delivered_messages || 0)} delivered`
    },
    {
      title: 'Delivery Rate',
      value: `${(overviewData.avg_delivery_rate || kpis.delivery_rate || 0).toFixed(1)}%`,
      icon: Target,
      color: 'bg-green-500',
      change: '+2%'
    },
    {
      title: 'Submit Rate',
      value: `${(overviewData.avg_submit_rate || 0).toFixed(1)}%`,
      icon: Zap,
      color: 'bg-yellow-500',
      change: '+1%'
    },
    {
      title: 'Total Revenue',
      value: formatValue(overviewData.total_revenue || kpis.total_revenue || 0, 'currency'),
      icon: DollarSign,
      color: 'bg-purple-500',
      change: '+8%',
      ...(isCustomer ? {} : { subtitle: `Profit: ${formatValue(overviewData.total_profit || kpis.profit_margin || 0, 'currency')}` })
    },
    {
      title: 'Active Customers',
      value: formatNumber(overviewData.unique_customers || kpis.unique_customers || 0),
      icon: Users,
      color: 'bg-indigo-500',
      change: '+5%',
      ...(isCustomer ? {} : { subtitle: `${formatNumber(overviewData.unique_vendors || kpis.unique_vendors || 0)} vendors` })
    },
    {
      title: 'Minified URLs',
      value: formatNumber(overviewData.total_urls || 0),
      icon: Link,
      color: 'bg-pink-500',
      change: '+15%'
    },
    {
      title: 'Platform Ingress',
      value: formatValue(overviewData.avg_platform_ingress_ms || 0, 'time'),
      icon: Clock,
      color: 'bg-orange-500',
      subtitle: 'timestamp → received'
    },
    {
      title: 'Vendor ACK',
      value: formatValue(overviewData.avg_vendor_ack_ms || 0, 'time'),
      icon: Zap,
      color: 'bg-amber-500',
      subtitle: 'submit_resp → submit'
    },
    {
      title: 'End-to-End',
      value: formatValue(overviewData.avg_e2e_ms || 0, 'time'),
      icon: Activity,
      color: 'bg-rose-500',
      subtitle: 'delivered → received'
    },
    {
      title: 'Delivered-Only',
      value: formatValue(overviewData.avg_delivered_only_ms || 0, 'time'),
      icon: CheckCircle,
      color: 'bg-emerald-500',
      subtitle: 'only delivered msgs'
    },
    {
      title: 'Failed Messages',
      value: formatNumber(kpis.failed_messages || 0),
      icon: AlertTriangle,
      color: 'bg-red-500',
      change: '-3%'
    },
    {
      title: 'Domestic Traffic',
      value: formatNumber(overviewData.domestic_traffic || 0),
      icon: Globe,
      color: 'bg-teal-500',
      change: '+3%'
    },
    {
      title: 'Fake DLR Count',
      value: formatNumber(overviewData.fake_dlr_count || 0),
      icon: AlertTriangle,
      color: 'bg-gray-500',
      change: '0%'
    },
    {
      title: 'MO Traffic',
      value: formatNumber(overviewData.mo_count || 0),
      icon: ArrowDownLeft,
      color: 'bg-cyan-500',
      subtitle: overviewData.total_messages
        ? `${((overviewData.mo_count || 0) * 100 / overviewData.total_messages).toFixed(1)}% of total`
        : 'mobile-originated'
    }
  ];

  // Merge time series data with htable chart data
  const mergedTimeSeriesData = timeSeriesData.map((item, index) => ({
    ...item,
    ...(chartData[index] || {})
  }));

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">CDR Analytics Dashboard</h1>
          <p className="text-gray-600">
          </p>
        </div>
        <div className="mb-8 flex items-center space-x-4">
          <button
            onClick={loadDashboardData}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => exportData('csv')}
            className="flex items-center space-x-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      <hr className="border-t border-gray-300" />

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Bar */}
        <div className="bg-white rounded-lg shadow mb-8 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-gray-500" />
              <select 
                value={filters.time_range}
                onChange={(e) => {
                  const newTimeRange = e.target.value;
                  if (newTimeRange !== 'custom') {
                    // For preset ranges, update and load immediately
                    setFilters({ time_range: newTimeRange, start_date: '', end_date: '' });
                  } else {
                    // For custom, just switch mode without loading
                    setFilters(prev => ({ ...prev, time_range: newTimeRange }));
                  }
                }}
                className="border rounded px-3 py-2 min-w-[150px]"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_90_days">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            
            {filters.time_range === 'custom' && (
              <>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">From:</label>
                  <input
                    type="date"
                    value={filters.start_date || ''}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      setFilters(prev => ({
                        ...prev, 
                        start_date: newStartDate,
                        // Clear end_date if it becomes less than start_date
                        ...(prev.end_date && newStartDate > prev.end_date && { end_date: '' })
                      }));
                    }}
                    className="border rounded px-3 py-2"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">To:</label>
                  <input
                    type="date"
                    value={filters.end_date || ''}
                    min={filters.start_date || undefined}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, end_date: e.target.value }));
                    }}
                    className="border rounded px-3 py-2"
                    disabled={!filters.start_date}
                  />
                </div>
                
                {/* Validation Messages */}
                {filters.time_range === 'custom' && !filters.start_date && (
                  <div className="text-amber-600 text-sm flex items-center gap-1">
                    <AlertTriangle size={14} />
                    Please select a start date
                  </div>
                )}
                {filters.time_range === 'custom' && filters.start_date && !filters.end_date && (
                  <div className="text-amber-600 text-sm flex items-center gap-1">
                    <AlertTriangle size={14} />
                    Please select an end date
                  </div>
                )}
                {filters.start_date && filters.end_date && filters.end_date < filters.start_date && (
                  <div className="text-red-600 text-sm flex items-center gap-1">
                    <AlertTriangle size={14} />
                    End date must be after start date
                  </div>
                )}
              </>
            )}
            
            {/* Apply Button - enabled based on validation */}
            <button 
              onClick={loadDashboardData}
              disabled={
                filters.time_range === 'custom' && (
                  !filters.start_date || 
                  !filters.end_date || 
                  filters.end_date < filters.start_date
                )
              }
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              title={
                filters.time_range === 'custom' && (!filters.start_date || !filters.end_date)
                  ? 'Please select both start and end dates'
                  : 'Apply selected date range'
              }
            >
              <RefreshCw size={16} />
              {filters.time_range === 'custom' ? 'Apply Custom Range' : 'Refresh'}
            </button>
            
            {/* Export Button */}
            <button 
              onClick={() => exportData('csv')}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2 transition-colors"
            >
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        {/* Unified KPI Cards Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8">
          {unifiedKpiCards
            .filter(card => card.value !== '0' && card.value !== '$0')
            .filter(card => !isCustomer || card.title !== 'Vendor ACK')
            .map((card, index) => (
            <KPICard
              key={index}
              title={card.title}
              value={card.value}
              icon={card.icon}
              color={card.color}
              change={card.change}
              subtitle={card.subtitle}
            />
          ))}
        </div>

        {/* Main Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Unified Trend Analysis */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Multi-Metric Trend Analysis</h3>
              <div className="flex space-x-2">
                {[
                  { key: 'total_messages', label: 'Messages' },
                  { key: 'revenue', label: 'Revenue' },
                  { key: 'delivery_rate', label: 'Delivery %' },
                  { key: 'avg_response_time', label: 'Response Time' },
                  { key: 'total_urls', label: 'URLs' }
                ].map(metric => (
                  <button
                    key={metric.key}
                    onClick={() => setActiveMetric(metric.key)}
                    className={`px-3 py-1 text-xs rounded-md ${
                      activeMetric === metric.key
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {metric.label}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mergedTimeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey={activeMetric} 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  name={activeMetric.charAt(0).toUpperCase() + activeMetric.slice(1).replace('_', ' ')}
                />
                {activeMetric === 'total_messages' && (
                  <>
                    <Line type="monotone" dataKey="delivered_messages" stroke="#10B981" strokeWidth={2} name="Delivered" />
                    <Line type="monotone" dataKey="failed_messages" stroke="#EF4444" strokeWidth={2} name="Failed" />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Enhanced Channel Distribution */}
          {channelData && channelData.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Channel Performance Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={channelData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage, avg_response_time }) => 
                      `${name} ${percentage}% (${avg_response_time?.toFixed(0)}ms)`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="percentage"
                    onClick={(entry) => handleDrillDown('channel', entry.name)}
                  >
                    {channelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 50%)`} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [
                    `${value}%`,
                    `${props.payload.messages?.toLocaleString()} messages, ${props.payload.avg_response_time?.toFixed(0)}ms avg`
                  ]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Revenue & Performance Combined */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Revenue & Delivery Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={mergedTimeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="revenue" fill="#F59E0B" name="Revenue ($)" />
                <Line yAxisId="right" type="monotone" dataKey="delivery_rate" stroke="#8B5CF6" strokeWidth={3} name="Delivery Rate (%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Combined Performance Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Unified Performance Summary</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Average Delivery Rate</span>
                <span className="font-semibold">{(overviewData.avg_delivery_rate || kpis.delivery_rate || 0).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Revenue</span>
                <span className="font-semibold text-green-600">{formatValue(overviewData.total_revenue || kpis.total_revenue || 0, 'currency')}</span>
              </div>
              {!isCustomer && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Cost</span>
                  <span className="font-semibold">{formatValue(kpis.total_cost || 0, 'currency')}</span>
                </div>
              )}
              {!isCustomer && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Profit Margin</span>
                  <span className="font-semibold text-green-600">
                    {formatValue(overviewData.total_profit || kpis.profit_margin || 0, 'currency')}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Failed Messages</span>
                <span className="font-semibold text-red-600">
                  {formatNumber(kpis.failed_messages || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Avg Response Time</span>
                <span className="font-semibold">{formatValue(overviewData.avg_response_time || 0, 'time')}</span>
              </div>
              {!isCustomer && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Fake DLR Count</span>
                  <span className="font-semibold text-orange-600">{formatNumber(overviewData.fake_dlr_count || 0)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Performance Trends Area Chart */}
        {performanceTrends.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">30-Day Performance Trends</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={performanceTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="avg_response_time" 
                  stackId="1"
                  stroke="#8884d8" 
                  fill="#8884d8"
                  name="Response Time (ms)"
                />
                <Area 
                  type="monotone" 
                  dataKey="total_urls" 
                  stackId="2"
                  stroke="#82ca9d" 
                  fill="#82ca9d"
                  name="URLs Created"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Bottom Section Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Enhanced Top Customers */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Top Performing Customers</h3>
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Messages</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">URLs</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg RT</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topCustomers.length > 0 && topCustomers.map((customer, index) => (
                      <tr 
                        key={index} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleDrillDown('customer', customer.name)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {customer.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatNumber(customer.messages)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatValue(customer.revenue, 'currency')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatNumber(customer.total_urls || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatValue(customer.avg_response_time || 0, 'time')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>


          {!isCustomer && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Top Performing Vendors</h3>
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Messages</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">URLs</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg RT</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topVendors.length > 0 && topVendors.map((vendor, index) => (
                      <tr 
                        key={index} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleDrillDown('customer', vendor.name)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {vendor.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatNumber(vendor.messages)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatValue(vendor.revenue, 'currency')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatNumber(vendor.total_urls || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatValue(vendor.avg_response_time || 0, 'time')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}

          {/* Enhanced Region Performance Details */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Region Performance Breakdown</h3>
              <div className="space-y-4">
                {regionData.length > 0 && regionData.map((channel, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleDrillDown('channel', channel.name)}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: `hsl(${index * 45}, 70%, 50%)` }}
                      ></div>
                      <div>
                        <span className="font-medium">{channel.name}</span>
                        <div className="text-xs text-gray-500">
                          {formatNumber(channel.total_urls || 0)} URLs • {formatNumber(channel.messages)} msgs
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{channel.percentage}%</div>
                      <div className="text-sm text-gray-500">
                        {formatValue(channel.revenue || 0, 'currency')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatValue(channel.avg_response_time || 0, 'time')} avg
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Enhanced Channel Performance Details */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Channel Performance Breakdown</h3>
              <div className="space-y-4">
                {channelData.length > 0 && channelData.map((channel, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleDrillDown('channel', channel.name)}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: `hsl(${index * 45}, 70%, 50%)` }}
                      ></div>
                      <div>
                        <span className="font-medium">{channel.name}</span>
                        <div className="text-xs text-gray-500">
                          {formatNumber(channel.total_urls || 0)} URLs • {formatNumber(channel.messages)} msgs
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{channel.percentage}%</div>
                      <div className="text-sm text-gray-500">
                        {formatValue(channel.revenue || 0, 'currency')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatValue(channel.avg_response_time || 0, 'time')} avg
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="fixed bottom-4 right-4 z-40">
          <SchedulerWidget isVisible={true} position="static"/>
        </div>
      </div>
    </div>
  );
};

export default UnifiedDashboard;